import React from 'react';
import {
  Save, Eye, Grid, ArrowLeft, Check, Loader2, AlertCircle, Lock,
  ChevronDown, ChevronUp, ChevronRight, PanelLeftOpen, Edit2,
  GitBranch, PlayCircle, Magnet, Ruler, Crosshair, Maximize2,
  Undo2, Redo2, Settings as SettingsIcon
} from 'lucide-react';

const BuilderToolbar = ({
  reviewReadOnly = false,
  onBack,
  localAssessmentConfig,
  editingAssessmentName,
  assessmentNameValue,
  setAssessmentNameValue,
  saveAssessmentName,
  cancelEditingAssessmentName,
  startEditingAssessmentName,
  saveStatus,
  lastSaved,
  showGrid,
  setShowGrid,
  snapToGrid,
  setSnapToGrid,
  showAlignmentGuides,
  setShowAlignmentGuides,
  showPositionOverlay,
  setShowPositionOverlay,
  showOutOfBoundsWarning,
  fitElementsToCanvas,
  sidebarPosition,
  setSidebarPosition,
  saving,
  handleSave,
  showSaveDropdown,
  setShowSaveDropdown,
  setPublishName,
  setShowPublishModal,
  enterPreviewMode,
  currentScreenIndex,
  showPreviewDropdown,
  setShowPreviewDropdown,
  showVersionsDropdown,
  setShowVersionsDropdown,
  canUndo = false,
  canRedo = false,
  onUndo = () => {},
  onRedo = () => {},
  onOpenSettings
}) => {
  return (
    <div className="bg-gradient-to-r from-white dark:from-gray-800 via-gray-50 dark:via-gray-900 to-white dark:to-gray-800 border-b border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between gap-4 flex-shrink-0 shadow-sm dark:shadow-gray-900/20">
      {/* Left Section */}
      <div className="flex min-w-0 shrink items-center space-x-4">
        <button
          onClick={onBack}
          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 group"
          title="Back"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform duration-200" />
        </button>
        {!reviewReadOnly && (
          <>
            <div className="border-l border-gray-300 h-8"></div>
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={onUndo}
                disabled={!canUndo}
                className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors disabled:opacity-40 disabled:pointer-events-none disabled:hover:bg-transparent"
                title="Undo (Ctrl+Z)"
                aria-label="Undo"
              >
                <Undo2 className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={onRedo}
                disabled={!canRedo}
                className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors disabled:opacity-40 disabled:pointer-events-none disabled:hover:bg-transparent"
                title="Redo (Ctrl+Y or Ctrl+Shift+Z)"
                aria-label="Redo"
              >
                <Redo2 className="w-5 h-5" />
              </button>
            </div>
          </>
        )}
        <div className="border-l border-gray-300 h-8"></div>

        {/* Editable Assessment Name */}
        <div className="flex items-center group">
          {editingAssessmentName ? (
            <input
              type="text"
              value={assessmentNameValue}
              onChange={(e) => setAssessmentNameValue(e.target.value)}
              onBlur={saveAssessmentName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveAssessmentName();
                } else if (e.key === 'Escape') {
                  cancelEditingAssessmentName();
                }
              }}
              autoFocus
              className="text-sm font-medium px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-800 border-2 border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
              style={{ minWidth: '200px' }}
            />
          ) : (
            <>
              <h1
                className={`text-xl font-bold text-gray-800 transition-colors ${reviewReadOnly ? '' : 'cursor-pointer hover:text-primary-600'}`}
                onDoubleClick={reviewReadOnly ? undefined : startEditingAssessmentName}
                title={reviewReadOnly ? undefined : 'Double-click to rename assessment'}
              >
                {localAssessmentConfig?.name || 'Assessment Builder'}
              </h1>
              {reviewReadOnly && (
                <span className="ml-2 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-lg flex items-center border border-blue-200">
                  <Lock className="w-3 h-3 mr-1" />
                  Read-only review
                </span>
              )}
              {localAssessmentConfig?.publication_status === 'published' && (
                <span className="ml-2 px-2.5 py-1 bg-gradient-to-r from-emerald-500 to-primary-500 text-white text-xs font-semibold rounded-lg flex items-center shadow-sm dark:shadow-gray-900/20">
                  <Lock className="w-3 h-3 mr-1" />
                  Published v{localAssessmentConfig?.version || '1.0'}
                </span>
              )}
              {!reviewReadOnly && localAssessmentConfig?.publication_status !== 'published' && (
                <button
                  onClick={startEditingAssessmentName}
                  className="ml-2 p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                  title="Rename assessment"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          )}
        </div>
        
        {/* Save Status */}
        {!reviewReadOnly && (
        <div className="flex items-center ml-4">
          {saveStatus === 'saving' && (
            <div className="flex items-center text-xs text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg">
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              <span>Saving...</span>
            </div>
          )}
          {saveStatus === 'saved' && lastSaved && (
            <div className="flex items-center text-xs text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg">
              <Check className="w-3.5 h-3.5 mr-1" />
              <span>Saved</span>
            </div>
          )}
          {saveStatus === 'unsaved' && (
            <div className="flex items-center text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-lg">
              <AlertCircle className="w-3.5 h-3.5 mr-1" />
              <span>Unsaved</span>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Center — canvas tools (device preset lives in Layout tab) */}
      <div className="flex flex-1 justify-center items-center min-w-0">
        {!reviewReadOnly && (
        <div className="flex items-center space-x-1 rounded-xl border border-gray-200/80 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 px-1 py-0.5 shadow-sm dark:shadow-gray-900/20">
          <label 
            className={`p-1.5 rounded cursor-pointer transition-colors ${showGrid ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'}`}
            title="Toggle Grid"
          >
            <input
              type="checkbox"
              checked={showGrid}
              onChange={(e) => setShowGrid(e.target.checked)}
              className="sr-only"
            />
            <Grid className="w-4 h-4" />
          </label>
          <label 
            className={`p-1.5 rounded cursor-pointer transition-colors ${snapToGrid ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'}`}
            title="Toggle Snap to Grid"
          >
            <input
              type="checkbox"
              checked={snapToGrid}
              onChange={(e) => setSnapToGrid(e.target.checked)}
              className="sr-only"
            />
            <Magnet className="w-4 h-4" />
          </label>
          <label 
            className={`p-1.5 rounded cursor-pointer transition-colors ${showAlignmentGuides ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'}`}
            title="Toggle Alignment Guides"
          >
            <input
              type="checkbox"
              checked={showAlignmentGuides}
              onChange={(e) => setShowAlignmentGuides(e.target.checked)}
              className="sr-only"
            />
            <Ruler className="w-4 h-4" />
          </label>
          <label 
            className={`p-1.5 rounded cursor-pointer transition-colors ${showPositionOverlay ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'}`}
            title="Toggle Position Overlay"
          >
            <input
              type="checkbox"
              checked={showPositionOverlay}
              onChange={(e) => setShowPositionOverlay(e.target.checked)}
              className="sr-only"
            />
            <Crosshair className="w-4 h-4" />
          </label>
          
          {/* Fit to Canvas Button */}
          <button
            onClick={() => fitElementsToCanvas('proportional')}
            className={`p-1.5 rounded transition-all ${
              showOutOfBoundsWarning 
                ? 'bg-amber-500 text-white hover:bg-amber-600' 
                : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600'
            }`}
            title={showOutOfBoundsWarning 
              ? "Elements are outside canvas bounds - click to auto-fit" 
              : "Fit all elements within canvas bounds"}
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
        )}
      </div>

      {/* Right Section - Actions */}
      <div className="flex shrink-0 items-center space-x-2">
        {/* Sidebar Position Controls (hidden in review — no sidebars) */}
        {!reviewReadOnly && (
        <div className="flex items-center space-x-0.5 mr-2 border-r border-gray-200 pr-2">
          <button
            onClick={() => setSidebarPosition('left')}
            className={`p-1.5 rounded-lg transition-all ${
              sidebarPosition === 'left'
                ? 'bg-primary-500 text-white shadow-sm dark:shadow-gray-900/20'
                : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'
            }`}
            title="Left Sidebar"
          >
            <PanelLeftOpen className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setSidebarPosition('right')}
            className={`p-1.5 rounded-lg transition-all ${
              sidebarPosition === 'right'
                ? 'bg-primary-500 text-white shadow-sm dark:shadow-gray-900/20'
                : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'
            }`}
            title="Right Sidebar"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setSidebarPosition('bottom')}
            className={`p-1.5 rounded-lg transition-all ${
              sidebarPosition === 'bottom'
                ? 'bg-primary-500 text-white shadow-sm dark:shadow-gray-900/20'
                : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'
            }`}
            title="Bottom Panel"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>
        )}

        {!reviewReadOnly && onOpenSettings && (
          <button
            type="button"
            onClick={onOpenSettings}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors mr-1"
            title="Assessment settings"
            aria-label="Assessment settings"
          >
            <SettingsIcon className="w-4 h-4" />
          </button>
        )}

        {/* Save/Publish Split Button */}
        {!reviewReadOnly && (
        <div className="relative inline-flex">
          {/* Main Save Button - Quick save without version snapshot */}
          <button
            onClick={() => handleSave(true, false)}
            disabled={saving}
            className="inline-flex items-center px-3.5 py-2 text-xs font-semibold text-white bg-primary-600 rounded-l-lg hover:bg-primary-700 disabled:opacity-50 transition-all shadow-sm dark:shadow-gray-900/20"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? 'Saving...' : 'Save'}
          </button>
          
          {/* Dropdown Arrow Button */}
          <button
            onClick={() => setShowSaveDropdown(!showSaveDropdown)}
            disabled={saving}
            className="inline-flex items-center px-2 py-2 text-xs font-semibold text-white bg-primary-600 rounded-r-lg hover:bg-primary-700 disabled:opacity-50 transition-all shadow-sm dark:shadow-gray-900/20 border-l border-primary-500"
          >
            <ChevronDown className="w-3 h-3" />
          </button>
          
          {showSaveDropdown && (
            <>
              {/* Backdrop to close dropdown */}
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowSaveDropdown(false)}
              />
              
              {/* Dropdown Menu */}
              <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 py-1 z-50">
                <button
                  onClick={() => {
                    handleSave(true, false);
                    setShowSaveDropdown(false);
                  }}
                  disabled={saving}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center"
                  title="Quick save your current work without creating a version history entry"
                >
                  <Save className="w-4 h-4 mr-2 text-gray-500" />
                  Save as Draft
                </button>
                
                <button
                  onClick={async () => {
                    await handleSave(false, true);
                    setShowSaveDropdown(false);
                  }}
                  disabled={saving}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center"
                  title="Save and create a restorable backup in Version History"
                >
                  <GitBranch className="w-4 h-4 mr-2 text-gray-500" />
                  Save & Create Snapshot
                </button>
                
                {localAssessmentConfig?.publication_status !== 'published' && (
                  <button
                    onClick={async () => {
                      await handleSave(true);
                      setShowSaveDropdown(false);
                      setTimeout(() => {
                        setPublishName('');
                        setShowPublishModal(true);
                      }, 500);
                    }}
                    disabled={saving}
                    className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50 flex items-center border-t border-gray-100 dark:border-gray-700"
                    title="Lock this version and make it available for use in studies"
                  >
                    <Check className="w-4 h-4 mr-2 text-blue-600" />
                    Save & Publish
                  </button>
                )}
                
                {localAssessmentConfig?.publication_status === 'published' && (
                  <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100 dark:border-gray-700">
                    <Lock className="w-3 h-3 inline mr-1" />
                    Published (locked)
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        )}

        <div className="relative inline-flex">
          <button
            onClick={() => enterPreviewMode(0)}
            className="inline-flex items-center px-3.5 py-2 text-xs font-medium text-gray-600 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-l-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 transition-all"
            title="Preview from beginning"
          >
            <Eye className="w-3.5 h-3.5 mr-1.5" />
            Preview
          </button>
          <button
            onClick={() => setShowPreviewDropdown(!showPreviewDropdown)}
            className="inline-flex items-center px-2 py-2 text-xs font-medium text-gray-600 bg-white dark:bg-gray-800 border border-l-0 border-gray-200 dark:border-gray-700 rounded-r-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 transition-all"
          >
            <ChevronDown className="w-3 h-3" />
          </button>
          {showPreviewDropdown && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowPreviewDropdown(false)}
              />
              <div className="absolute right-0 mt-8 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 z-20">
                <button
                  onClick={() => {
                    enterPreviewMode(0);
                    setShowPreviewDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center rounded-t-md"
                >
                  <PlayCircle className="w-4 h-4 mr-2 text-gray-500" />
                  From Beginning
                </button>
                <button
                  onClick={() => {
                    enterPreviewMode(currentScreenIndex);
                    setShowPreviewDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center rounded-b-md"
                >
                  <Eye className="w-4 h-4 mr-2 text-gray-500" />
                  From Current Screen
                </button>
              </div>
            </>
          )}
        </div>

        {/* Version History button - Toggle dropdown */}
        {!reviewReadOnly && (
        <button
          onClick={() => setShowVersionsDropdown(!showVersionsDropdown)}
          className={`inline-flex items-center px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
            showVersionsDropdown
              ? 'text-white bg-primary-500 shadow-sm dark:shadow-gray-900/20'
              : 'text-gray-600 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600'
          }`}
          title="View version history and restore previous versions"
        >
          <GitBranch className="w-3.5 h-3.5 mr-1.5" />
          Versions
          {showVersionsDropdown ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
        </button>
        )}
      </div>
    </div>
  );
};

export default BuilderToolbar;
